import { getAuthHeaders } from "../utils/authHeaders";
import { API_URL } from "../config/api";

export interface Notification {
  _id: string;
  userId: string;
  type: "job_alert" | "application_update" | "new_application" | "message" | "system";
  title: string;
  message: string;
  read: boolean;
  actionUrl?: string;
  relatedId?: string;
  relatedType?: "job" | "application" | "message";
  createdAt: string;
  updatedAt: string;
}

export interface NotificationResponse {
  notifications: Notification[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
  };
}

// Get all notifications for current user
export const getNotifications = async (
  page: number = 1,
  limit: number = 20,
  read?: boolean
): Promise<NotificationResponse> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (read !== undefined) {
      params.append("read", read.toString());
    }

    const response = await fetch(`${API_URL}/notifications?${params}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch notifications");
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching notifications:", error);
    throw error;
  }
};

// Get unread notification count
export const getUnreadCount = async (): Promise<number> => {
  try {
    const response = await fetch(`${API_URL}/notifications/unread-count`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch unread count");
    }

    const data = await response.json();
    return data.unreadCount;
  } catch (error) {
    console.error("Error fetching unread count:", error);
    return 0;
  }
};

// Mark single notification as read
export const markAsRead = async (id: string): Promise<Notification> => {
  try {
    const response = await fetch(`${API_URL}/notifications/${id}/read`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to mark notification as read");
    }

    return response.json();
  } catch (error) {
    console.error("Error marking notification as read:", error);
    throw error;
  }
};

// Mark all notifications as read
export const markAllAsRead = async (): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/notifications/read-all`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to mark all notifications as read");
    }
  } catch (error) {
    console.error("Error marking all as read:", error);
    throw error;
  }
};

// Delete a notification
export const deleteNotification = async (id: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/notifications/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to delete notification");
    }
  } catch (error) {
    console.error("Error deleting notification:", error);
    throw error;
  }
};

// Delete all notifications
export const deleteAllNotifications = async (): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/notifications`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to delete all notifications");
    }
  } catch (error) {
    console.error("Error deleting all notifications:", error);
    throw error;
  }
};
